import type { Feed, FeedItem, FeedResponse } from '@knocklabs/client'
import { chunk } from 'lodash-es'
import { broadcastArchivedInboxItems } from '#src/hooks/inbox/inboxSyncChannel.ts'
import { KNOCK_PAGE_SIZE } from '#src/hooks/useNotificationItems.ts'

// One batch archive request carries at most this many ids, matching the
// backfill script. The SDK sends whatever it is given as a single request, and
// a walk of the whole feed can supersede more than one batch's worth at once.
const ARCHIVE_BATCH_SIZE = 100

// Removal contract, deliberately asymmetric: a row leaves the list only because
// something positively asserted it is archived — an event naming its id, or the
// server returning it in an archived-only page. Absence from a page is never
// treated as removal, so a live row can never be deleted by inference. Missing a
// removal is acceptable: the existing non-engaged reconcile catches it, and the
// behaviour it replaces removed nothing at all while a detail panel was open.

// Archives through the SDK, then tells other tabs only about the ids the server
// actually archived. `batchUpdateStatuses` resolves to Message[] on success and to
// an error object on a sub-500 failure, so a non-array result means nothing landed;
// 5xx and network failures reject, and every caller voids this, so swallow those too.
// An empty array confirms nothing, which is exactly when broadcasting would be worst.
// Returns the ids the server confirmed, so a caller that memoizes "already handled"
// only memoizes work that landed.
// `shouldContinue` is read before every batch rather than once, because an
// archive of more than one batch's worth awaits between them: a caller whose
// kill switch goes off mid-archive must not send the batches that are left.
export async function archiveInboxItemsVerified(
  feedClient: Feed,
  items: FeedItem[],
  shouldContinue?: () => boolean
): Promise<string[]> {
  if (items.length === 0) {
    return []
  }
  const confirmed = new Set<string>()
  // Every batch failing is the whole archive failing, and must not publish an
  // empty confirmation: that is a positive statement that nothing is archived.
  let responded = false
  for (const batch of chunk(items, ARCHIVE_BATCH_SIZE)) {
    if (shouldContinue && !shouldContinue()) {
      break
    }
    const result = await feedClient.markAsArchived(batch).catch(() => undefined)
    if (!Array.isArray(result)) {
      continue
    }
    responded = true
    for (const message of result) {
      confirmed.add((message as { id: string }).id)
    }
  }
  if (!responded) {
    return []
  }
  const itemIds = items.flatMap((item) =>
    confirmed.has(item.id) ? [item.id] : []
  )
  broadcastArchivedInboxItems(feedClient, itemIds)
  return itemIds
}

// Reads the archived-only page without touching feedClient.store, mirroring the
// URL feed.fetch() builds. Deliberately one page: this is a discovery mechanism,
// not a convergence guarantee, and anything it misses the full reconcile catches.
export async function probeArchivedInboxIds(
  feedClient: Feed
): Promise<string[] | undefined> {
  const userId = feedClient.knock.userId
  if (!userId) {
    return undefined
  }
  const result = await feedClient.knock.client().makeRequest({
    method: 'GET',
    url: `/v1/users/${userId}/feeds/${feedClient.feedId}`,
    params: { archived: 'only', page_size: KNOCK_PAGE_SIZE },
  })
  if (result.statusCode === 'error' || !result.body) {
    return undefined
  }
  const body = result.body as FeedResponse
  return body.entries.map((entry) => entry.id)
}

interface RemoveInboxItemsResult {
  removed: number
  withheld: string | undefined
}

// Drops the given ids from the store in one functional update, preserving
// pageInfo, loading, networkStatus and metadata. Metadata is left to the server:
// the SDK already adjusts the counts locally when this tab archives, but a count
// derived here would be blind to items outside the loaded page and to the row this
// function deliberately withholds, so only an absolute server snapshot is honest.
// Returns the protected id when one was withheld, so the caller can re-apply the
// assertion once the row is no longer open rather than dropping it on the floor.
export function removeInboxItems({
  feedClient,
  itemIds,
  protectedItemId,
}: {
  feedClient: Feed
  itemIds: string[]
  protectedItemId: string | undefined
}): RemoveInboxItemsResult {
  const withheld =
    protectedItemId !== undefined && itemIds.includes(protectedItemId)
      ? protectedItemId
      : undefined
  const removable = new Set(itemIds.filter((id) => id !== protectedItemId))
  if (removable.size === 0) {
    return { removed: 0, withheld }
  }
  let removed = 0
  feedClient.store.setState((state) => {
    const kept = state.items.filter((item) => !removable.has(item.id))
    removed = state.items.length - kept.length
    if (removed === 0) {
      return state
    }
    return { ...state, items: kept }
  })
  return { removed, withheld }
}
